import { useState, useEffect, useMemo } from "react";
import { MdTrendingUp, MdFileDownload, MdWork } from "react-icons/md";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";

const PALETTE = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#3b82f6", "#8b5cf6"];

const exportToCsv = (filename, headers, rows) => {
  const encode = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val).replace(/"/g, '""');
    return `"${str}"`;
  };
  const allRows = [headers, ...rows];
  const csv = allRows.map((r) => r.map(encode).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "8px 13px",
        boxShadow: "0 4px 14px rgba(0,0,0,.1)",
        fontSize: "0.8rem",
      }}
    >
      {label && <p style={{ fontWeight: 700, marginBottom: 4 }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill, fontWeight: 600 }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

const roleLabel = (role) => {
  if (role === "super_admin") return "Super Admin";
  if (role === "admin") return "Admin";
  if (role === "accounts") return "Accounts";
  if (role === "hr") return "HR";
  return "Staff";
};

const StaffWorkloadReport = () => {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const reqs = [
          api.get("/tasks/admin/all", { params: { reportsView: 1 } }),
          api.get("/users"),
        ];
        const [tRes, uRes] = await Promise.all(reqs);
        setTasks(tRes.data.data || []);
        setUsers(uRes.data.data || []);
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) load();
  }, [isAdmin]);

  const staffUsers = useMemo(
    () => users,
    [users]
  );

  const workloadData = useMemo(() => {
    return staffUsers.map((u, idx) => {
      const relatedTasks = tasks.filter(
        (t) =>
          t.createdBy?._id === u._id || t.assignedTo?._id === u._id
      );
      const activeTasks = relatedTasks.filter((t) => t.status !== "done");
      return {
        id: u._id,
        name: u.name.split(" ")[0],
        total: relatedTasks.length,
        active: activeTasks.length,
        critical: activeTasks.filter((t) => t.priority === "critical").length,
        high: activeTasks.filter((t) => t.priority === "high").length,
        medium: activeTasks.filter((t) => t.priority === "medium").length,
        low: activeTasks.filter((t) => t.priority === "low").length,
        color: PALETTE[idx % PALETTE.length],
      };
    });
  }, [tasks, staffUsers]);

  const filteredData = useMemo(() => {
    if (!selectedUserId) return workloadData;
    return workloadData.filter((r) => r.id === selectedUserId);
  }, [workloadData, selectedUserId]);

  const metrics = useMemo(() => {
    if (!workloadData.length) {
      return {
        avgTasks: 0,
        avgActive: 0,
        overloadedCount: 0,
        freeCount: 0,
      };
    }
    const totalTasks = workloadData.reduce((sum, r) => sum + r.total, 0);
    const totalActive = workloadData.reduce((sum, r) => sum + r.active, 0);
    const avgTasks = Number((totalTasks / workloadData.length).toFixed(1));
    const avgActive = Number((totalActive / workloadData.length).toFixed(1));
    const overloadedCount = workloadData.filter(
      (r) => r.active > avgActive && r.active > 0
    ).length;
    const freeCount = workloadData.filter((r) => r.active === 0).length;
    return {
      avgTasks,
      avgActive,
      overloadedCount,
      freeCount,
    };
  }, [workloadData]);

  if (!isAdmin) {
    return (
      <main className="page">
        <div className="card card-body">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MdTrendingUp size={26} color="var(--primary)" />
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Staff workload report
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "var(--text-muted)",
                  marginTop: 4,
                }}
              >
                This report is available for admin users only.
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="page">
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Loading staff workload...</span>
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <MdTrendingUp style={{ marginRight: 6 }} />
            Staff workload
          </h1>
          <p className="page-subtitle">
            Shows how tasks are distributed among staff and highlights imbalances.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() =>
            exportToCsv(
              "staff-workload.csv",
              [
                "Staff",
                "Total Tasks",
                "Active Tasks",
                "Critical",
                "High",
                "Medium",
                "Low",
              ],
              filteredData.map((r) => [
                r.name,
                r.total,
                r.active,
                r.critical,
                r.high,
                r.medium,
                r.low,
              ])
            )
          }
        >
          <MdFileDownload size={15} style={{ marginRight: 4 }} />
          Export Excel
        </button>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div
          className="card-body"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ minWidth: 220 }}>
            <label className="form-label">Select staff member</label>
            <select
              className="form-control"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">All staff</option>
              {staffUsers
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name} ({roleLabel(u.role)})
                  </option>
                ))}
            </select>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <div className="stat-card" style={{ minWidth: 150 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                Tasks per staff (avg)
              </div>
              <div className="stat-value">{metrics.avgTasks}</div>
            </div>
            <div className="stat-card" style={{ minWidth: 150 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                Active tasks per employee
              </div>
              <div className="stat-value">{metrics.avgActive}</div>
            </div>
            <div className="stat-card" style={{ minWidth: 150 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                Overloaded staff members
              </div>
              <div className="stat-value">{metrics.overloadedCount}</div>
            </div>
            <div className="stat-card" style={{ minWidth: 150 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                Free capacity employees
              </div>
              <div className="stat-value">{metrics.freeCount}</div>
            </div>
          </div>
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="card card-body">
          <div className="empty-state">
            <MdWork size={40} />
            <h3>No workload data</h3>
            <p>There are no open tasks at the moment.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="card card-body" style={{ marginBottom: 20 }}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={filteredData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: "0.72rem" }} />
                <Bar dataKey="active" name="Active tasks" fill="#6366f1" />
                <Bar dataKey="critical" name="Critical" fill="#ef4444" />
                <Bar dataKey="high" name="High" fill="#f97316" />
                <Bar dataKey="medium" name="Medium" fill="#f59e0b" />
                <Bar dataKey="low" name="Low" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid var(--border)",
                fontWeight: 700,
                fontSize: "0.9rem",
              }}
            >
              Staff workload details
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Total Tasks</th>
                    <th>Active Tasks</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((r) => {
                    const status =
                      r.active === 0
                        ? "Free capacity"
                        : r.active > metrics.avgActive
                        ? "Overloaded"
                        : "Normal";
                    return (
                      <tr key={r.id}>
                        <td>{r.name}</td>
                        <td className="text-muted">{r.total}</td>
                        <td className="text-muted">{r.active}</td>
                        <td>
                          <span
                            className={`badge ${
                              status === "Overloaded"
                                ? "badge-critical"
                                : status === "Free capacity"
                                ? "badge-done"
                                : "badge-review"
                            }`}
                          >
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default StaffWorkloadReport;

